import { useApolloClient } from "@apollo/client";
import React, {
	useEffect,
	useState,
} from 'react';
import { head } from 'lodash';

import { useTransitAccountProductCatalog } from 'components/data/transit-account/ProductCatalog.query.js';
import CmsContentList from 'components/data/CmsContentList.js';
import FormHelper, { WireFormHelper } from 'utils/FormHelper.js';
import useFormHelper from "utils/form-helper/useFormHelper.js";
import {
	cartIsValid,
	useCartContext,
} from 'context/CartProvider.js';
import PreventDefault from 'utils/PreventDefault.js';

import Cart from "components/payments/Cart.js";
import { getPaymentMethods } from 'pages/account/PurchasePass.js';
import { usePurseBalanceContext } from 'context/PurseBalanceContext.js';
import {
	levels,
	noticeError,
} from "utils/Logger.js";
import { useFundingSourcesContext } from 'context/FundingSourcesContext.js';
import CartLeavePagePrompt from 'pages/account/purchase/CartLeavePagePrompt.js';
import { useTransitAccountIdContext } from "context/TransitAccountIdContext.js";
import { incrementErrorCmsKey } from 'components/account/purchases/ValuePurchaseTypeSelection.js';
import { PURCHASE_FLOW_STEP_1_FORM_ID } from 'components/account/reload-balance/consts.js';
import { STANDARD_PURCHASE_FLOW_STEPS } from 'pages/account/purchase/StandardPurchaseFlow.js';
import { useStepContext } from 'context/StepContext.js';
import ProductTakeOverLayout from 'pages/account/purchase/product-catalog-takeover/ProductTakeOverLayout.js';
import { object as yup_object } from "yup";
import { getPurchaseCatalogTabs } from 'pages/account/purchase/product-catalog-takeover/ProductTabs.js';
import { cms as purchaseCmsKeys } from 'pages/account/purchase/product-catalog-takeover/constants.js';
import CmsContentRenderedInline from "components/data/CmsContentRenderedInline.js";
import TransitAccountProductCatalogContext, { useTransitAccountProductCatalogContext } from "components/data/transit-account/ProductCatalogContext.js";

const cms = {
	emptySelectionError: "miscText.purchase-cart-error-empty",
	incrementErrorCmsKey,
};

const PurchaseProductCatalog = ({ cancelUrl }) => {
	const { step, nextStep } = useStepContext();

	const {
		cart,
		setNegativeBalance,
		saveShoppingCartId,
	} = useCartContext();

	const { initializeFundingSources } = useFundingSourcesContext();
	const [ submitting, setSubmitting ] = useState(false);
	const apolloClient = useApolloClient();
	const { purses, purseTotal, hasNegativeBalance, tokens } = usePurseBalanceContext();
	const tokenId = head(tokens)?.tokenId;

	const wSSubsystemPurse = head(purses);

	const transit_account_id = useTransitAccountIdContext();

	const {
		formRef,
		formHelper,
		setValidationState,
	} = useFormHelper({ getYupSchema: () =>  yup_object().shape({}) });

	const kickoffSubmit = async () => {
		setSubmitting(true);

		// Let normal validation run first
		if (!cartIsValid(cart)) {
			setValidationState({
				validationError: FormHelper.cmsKeyToJsx(cms.emptySelectionError),
			});
			setSubmitting(false);
			return;
		}

		// validate the TransUnion fingerprint
		try {
			await formHelper.startValidation(true);
		}
		catch (errorReport) {
			setSubmitting(false);
			noticeError(null, levels.verbose, errorReport, `Purchase Product Catalog with fingerprint failed`);
			formHelper.validationErrorHandler(errorReport);
			return;
		}

		let paymentMethodsRes;
		try {
			paymentMethodsRes = await getPaymentMethods({
				cart,
				apolloClient,
				subsystemAccountReference: transit_account_id,
				wSSubsystemPurse,
				tokenId,
			});
		} catch (errorReport) {
			// we're not redirecting anywhere. Prepare the form for the next submit.
			noticeError(null, levels.info, errorReport, `Get Payment Methods Failed`);
			formHelper.validationErrorHandler(errorReport);
			setSubmitting(false);
			return;
		}

		const {
			shoppingCartId,
			loadProductLineItemFunds,
			fundingSources,
			restrictFunding,
		} = paymentMethodsRes;

		saveShoppingCartId(shoppingCartId);
		initializeFundingSources({
			loadProductLineItemFunds,
			fundingSources, // might be empty
			restrictFunding,
		});

		setSubmitting(false);
		nextStep();
	};

	// When an account has a negative balance should we reflect the negative balance in the initial cart total
	useEffect(() => {
		if (hasNegativeBalance && !cart.negativeBalance) {
			setNegativeBalance(purseTotal);
		}
	}, [ hasNegativeBalance, cart.negativeBalance,purseTotal,setNegativeBalance ]);

	const productCatalog = useTransitAccountProductCatalogContext();

	const purchaseProductCatalogTabs = getPurchaseCatalogTabs({ formHelper, setValidationState, productCatalog });

	return (
		<CmsContentList list={Object.values(cms)}>{() =>
			<WireFormHelper formHelper={formHelper}>
				<CartLeavePagePrompt />
				<form
					id={PURCHASE_FLOW_STEP_1_FORM_ID}
					data-qa={PURCHASE_FLOW_STEP_1_FORM_ID}
					ref={formRef}
					onSubmit={PreventDefault(kickoffSubmit)}
				>
					<ProductTakeOverLayout
						showCancel
						showNickname
						showTabs
						title={<CmsContentRenderedInline
							contentKey={purchaseCmsKeys.purchasNewProduct}
							fallbackValue="Purchase a New Product"
						/>}
						steps={STANDARD_PURCHASE_FLOW_STEPS}
						currentStep={step}
						tabs={purchaseProductCatalogTabs}
						cancelLink={cancelUrl}
						unpaidFee={cart.unpaidFee}
					/>
					<Cart formHelper={formHelper} {...{ submitting }} />
				</form>
			</WireFormHelper>
		}</CmsContentList>
	);
};

const PurchaseProductCatalogWithContextProvider = ({ cancelUrl }) => {
	const subsystemAccountReference = useTransitAccountIdContext();
	const productCatalog = useTransitAccountProductCatalog({ subsystemAccountReference });

	return (
		<TransitAccountProductCatalogContext.Provider value={productCatalog}>
			<PurchaseProductCatalog {...{ cancelUrl }} />
		</TransitAccountProductCatalogContext.Provider>
	);
};

export default PurchaseProductCatalogWithContextProvider;
